import { DownloadParams } from '@/hooks/useDownload'
import { PermissionLevel } from '@/modules/Drive/utils/DrivePermission/permission'
import { ViewType } from '@/modules/Drive/components/ViewTypeSwitch'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { DownloadMultipleFilesParams } from '@/modules/Drive/types'
export enum Sections {
    All = 'all',
    MyDrive = 'my-drive',
    Recent = 'recent',
    Starred = 'starred',
    Shared = 'shared',
    RootDrive = 'root-drive',
}

interface DriveStore {
    sectionActive: Sections
    refresh: boolean
    currentPath: string[]
    currentRoot: string
    isDownload: boolean
    isUpload: boolean
    starredObjectKeys: string[]
    currentPermission?: PermissionLevel
    viewType: ViewType
    itemNames: Record<string, string>
    isSelectedMode: boolean
    isSelectedAll: boolean
    selectedItems: string[]
    setRefresh: () => void
    setSectionActive: (section: Sections) => void
    setCurrentPath: (newPath: string[]) => void
    appendToPath: (folderName: string) => void // Add a folder to the path
    popFromPath: () => void // Remove the last folder from the path
    setIsDownload: (state: boolean) => void
    setCurrentRoot: (root: string) => void
    setStarredObjectKeys: (keys: string[]) => void
    abortUpload: (fileName: string) => void
    setAbortUploadHandler: (handler: (fileName: string) => void) => void
    abortDownload: (fileName: string) => void
    setAbortDownloadHandler: (handler: (fileName: string) => void) => void
    downloadFile: (params: DownloadParams) => void
    setDownloadFile: (handler: (params: DownloadParams) => void) => void
    downloadMultipleItems: (params: DownloadMultipleFilesParams) => void
    setDownloadMultipleItems: (
        handler: (params: DownloadMultipleFilesParams) => void
    ) => void
    setCurrentPermission: (permission?: PermissionLevel) => void
    setViewType: (viewType: ViewType) => void
    appendItemName: (uuid: string, itemName: string) => void
    setIsSelectedMode: (state: boolean) => void
    setIsSelectedAll: (state: boolean) => void
    setSelectedItems: (items: string[]) => void
    appendSelectedItems: (item: string) => void
    removeSelectedItems: (item: string) => void
    clearSelectedItems: () => void
}

let abortUploadHandler: ((fileName: string) => void) | undefined
let abortDownloadHandler: ((fileName: string) => void) | undefined
let downloadFileHandler: ((params: DownloadParams) => void) | undefined
let downloadMultipleItemsHandler:
    | ((params: DownloadMultipleFilesParams) => void)
    | undefined

const useDriveStore = create<DriveStore>()(
    persist(
        (set) => ({
            refresh: false,
            setRefresh: () => set((state) => ({ refresh: !state.refresh })),
            sectionActive: Sections.All,
            setSectionActive: (section) =>
                set(() => ({
                    sectionActive: section,
                    sectionStyle: mapSectionToStyle(section), // Auto-update here
                })),
            currentPath: [],
            setCurrentPath: (newPath) => {
                set(() => ({
                    currentPath: newPath,
                }))
            },
            appendToPath: (folderName) =>
                set((state) => ({
                    currentPath: [...state.currentPath, folderName],
                })),
            popFromPath: () =>
                set((state) => ({
                    currentPath: state.currentPath.slice(0, -1),
                })),
            currentRoot: '',
            setCurrentRoot: (root) => set(() => ({ currentRoot: root })),
            isDownload: false,
            isUpload: false,
            setIsDownload: (state: boolean) =>
                set(() => ({ isDownload: state })),
            starredObjectKeys: [],
            setStarredObjectKeys: (keys) =>
                set(() => ({ starredObjectKeys: keys })),
            abortUpload: (fileName) => {
                if (abortUploadHandler) {
                    abortUploadHandler(fileName)
                } else {
                    console.warn('No abort handler registered')
                }
            },
            setAbortUploadHandler: (handler) => {
                abortUploadHandler = handler
            },
            abortDownload: (fileName) => {
                if (abortDownloadHandler) {
                    abortDownloadHandler(fileName)
                } else {
                    console.warn('No abort download handler registered')
                }
            },
            setAbortDownloadHandler: (handler) => {
                abortDownloadHandler = handler
            },
            downloadFile: (params) => {
                if (downloadFileHandler) {
                    downloadFileHandler(params)
                } else {
                    console.warn('No download handler registered')
                }
            },
            setDownloadFile: (handler) => {
                downloadFileHandler = handler
            },
            downloadMultipleItems: (params) => {
                if (downloadMultipleItemsHandler) {
                    downloadMultipleItemsHandler(params)
                } else {
                    console.warn(
                        'No download multiple items handler registered'
                    )
                }
            },
            setDownloadMultipleItems: (handler) => {
                downloadMultipleItemsHandler = handler
            },
            currentPermission: undefined,
            setCurrentPermission: (permission) =>
                set(() => ({ currentPermission: permission })),
            viewType: 'grid',
            setViewType: (viewType) => set(() => ({ viewType })),
            itemNames: {},
            appendItemName: (uuid, itemName) =>
                set((state) => ({
                    itemNames: { ...state.itemNames, [uuid]: itemName },
                })),
            isSelectedMode: false,
            setIsSelectedMode: (state: boolean) =>
                set(() => ({ isSelectedMode: state })),
            isSelectedAll: false,
            setIsSelectedAll: (state: boolean) =>
                set(() => ({ isSelectedAll: state })),
            selectedItems: [],
            setSelectedItems: (items: string[]) =>
                set(() => ({ selectedItems: items })),
            appendSelectedItems: (item: string) =>
                set((state) => ({
                    selectedItems: [...state.selectedItems, item],
                })),
            removeSelectedItems: (itemKey: string) =>
                set((state) => ({
                    selectedItems: state.selectedItems.filter(
                        (item) => item !== itemKey
                    ),
                })),
            clearSelectedItems: () => set(() => ({ selectedItems: [] })),
        }),
        {
            name: 'drive-store',
            storage: createJSONStorage(() => sessionStorage),
            partialize: (state) => ({
                starredObjectKeys: state.starredObjectKeys,
                currentPath: state.currentPath,
                sectionActive: state.sectionActive,
                currentRoot: state.currentRoot,
                itemNames: state.itemNames,
            }),
        }
    )
)

// Helper function to map section to style
function mapSectionToStyle(section: Sections): string {
    if (section === Sections.All) return 'h-fit'
    return 'h-screen'
}

export default useDriveStore
